import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import type { Unsubscribe } from 'firebase/firestore';
import { db } from '../firebase/config';
import type {
  TodoList,
  TodoItem,
  SubTask,
  CreateTodoListInput,
  CreateTodoItemInput,
  UpdateTodoItemInput,
  CreateSubTaskInput,
  InviteUserInput,
} from '@/types/todolist';
import type { SharedUser, SharePermission } from '@/types/thinkspace';
import type { User } from 'firebase/auth';
import { calculateNextDueDate } from '@/utils/recurrenceUtils';

const COLLECTION = 'todolists';

export const todolistsService = {
  // Get a single TodoList by ID
  getById: async (id: string): Promise<TodoList | null> => {
    const docRef = doc(db, COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    return { id: docSnap.id, ...docSnap.data() } as TodoList;
  },

  // Get all TodoLists owned by user
  getOwned: async (userId: string): Promise<TodoList[]> => {
    const q = query(
      collection(db, COLLECTION),
      where('ownerId', '==', userId),
      orderBy('updatedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as TodoList);
  },

  // Get all TodoLists shared with user
  getSharedWithMe: async (userEmail: string): Promise<TodoList[]> => {
    const allLists = await getDocs(collection(db, COLLECTION));
    return allLists.docs
      .map((d) => ({ id: d.id, ...d.data() }) as TodoList)
      .filter((list) =>
        list.sharedWith?.some(
          (share) => share.email.toLowerCase() === userEmail.toLowerCase() && share.status === 'accepted'
        )
      );
  },

  // Get pending invitations for user
  getPendingInvitations: async (userEmail: string): Promise<TodoList[]> => {
    const allLists = await getDocs(collection(db, COLLECTION));
    return allLists.docs
      .map((d) => ({ id: d.id, ...d.data() }) as TodoList)
      .filter((list) =>
        list.sharedWith?.some(
          (share) => share.email.toLowerCase() === userEmail.toLowerCase() && share.status === 'pending'
        )
      );
  },

  // Subscribe to a TodoList for real-time updates
  subscribe: (
    id: string,
    callback: (list: TodoList | null) => void,
    onError?: (error: Error) => void
  ): Unsubscribe => {
    const docRef = doc(db, COLLECTION, id);
    return onSnapshot(
      docRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          callback(null);
          return;
        }
        callback({ id: snapshot.id, ...snapshot.data() } as TodoList);
      },
      onError
    );
  },

  // Create a new TodoList
  create: async (input: CreateTodoListInput, user: User): Promise<string> => {
    const docRef = doc(collection(db, COLLECTION));
    const todoList: Omit<TodoList, 'id'> = {
      name: input.name,
      description: input.description || '',
      ownerId: user.uid,
      ownerEmail: user.email || '',
      ownerName: user.displayName || '',
      items: [],
      sharedWith: [],
      createdAt: serverTimestamp() as unknown as Timestamp,
      updatedAt: serverTimestamp() as unknown as Timestamp,
    };
    await setDoc(docRef, todoList);
    return docRef.id;
  },

  // Update TodoList metadata
  update: async (
    id: string,
    data: Partial<Pick<TodoList, 'name' | 'description'>>
  ): Promise<void> => {
    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  },

  // Delete a TodoList
  delete: async (id: string): Promise<void> => {
    const docRef = doc(db, COLLECTION, id);
    await deleteDoc(docRef);
  },

  // ============================================
  // Todo Item Operations
  // ============================================

  // Add a new todo item
  addItem: async (id: string, input: CreateTodoItemInput): Promise<TodoItem> => {
    const list = await todolistsService.getById(id);
    if (!list) throw new Error('TodoList not found');

    const maxOrder = list.items.reduce((max, item) => Math.max(max, item.order), -1);
    const isRecurring = !!input.recurrence;
    const seriesId = isRecurring ? crypto.randomUUID() : null;

    const newItem: TodoItem = {
      id: crypto.randomUUID(),
      title: input.title,
      description: input.description || '',
      completed: false,
      completedAt: null,
      dueDate: input.dueDate ? Timestamp.fromDate(input.dueDate) : null,
      priority: input.priority || null,
      order: maxOrder + 1,
      subtasks: [],
      // Recurrence fields
      recurrence: input.recurrence || null,
      isRecurring,
      seriesId,
      // Use Timestamp.now() instead of serverTimestamp() because serverTimestamp()
      // is not supported inside arrays in Firestore
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      items: [...list.items, newItem],
      updatedAt: serverTimestamp(),
    });

    return newItem;
  },

  // Update a todo item
  updateItem: async (
    id: string,
    itemId: string,
    updates: UpdateTodoItemInput
  ): Promise<void> => {
    const list = await todolistsService.getById(id);
    if (!list) throw new Error('TodoList not found');

    const updatedItems = list.items.map((item) => {
      if (item.id === itemId) {
        const updated = {
          ...item,
          ...updates,
          // Use Timestamp.now() instead of serverTimestamp() for array items
          updatedAt: Timestamp.now(),
        };

        // Auto-set completedAt if marking as completed and not already set
        if (updates.completed && !item.completed && !updates.completedAt) {
          updated.completedAt = Timestamp.now();
        }
        // Clear completedAt if marking as incomplete
        if (updates.completed === false) {
          updated.completedAt = null;
        }

        return updated;
      }
      return item;
    });

    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      items: updatedItems,
      updatedAt: serverTimestamp(),
    });
  },

  // Complete a recurring task and create the next occurrence
  completeRecurringItem: async (
    id: string,
    itemId: string
  ): Promise<TodoItem | null> => {
    const list = await todolistsService.getById(id);
    if (!list) throw new Error('TodoList not found');

    const item = list.items.find((i) => i.id === itemId);
    if (!item) throw new Error('TodoItem not found');

    // If not recurring, just mark complete and return
    if (!item.isRecurring || !item.recurrence || !item.dueDate) {
      await todolistsService.updateItem(id, itemId, {
        completed: true,
        completedAt: Timestamp.now(),
      });
      return null;
    }

    const completionDate = new Date();
    const currentDueDate = item.dueDate.toDate();
    const nextDueDate = calculateNextDueDate(currentDueDate, item.recurrence, completionDate);

    // Mark current item as completed
    const updatedItems = list.items.map((i) => {
      if (i.id === itemId) {
        return {
          ...i,
          completed: true,
          completedAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        };
      }
      return i;
    });

    // If there's a next occurrence, create a new item
    let newItem: TodoItem | null = null;
    if (nextDueDate) {
      const maxOrder = list.items.reduce((max, i) => Math.max(max, i.order), -1);
      newItem = {
        id: crypto.randomUUID(),
        title: item.title,
        description: item.description,
        completed: false,
        completedAt: null,
        dueDate: Timestamp.fromDate(nextDueDate),
        priority: item.priority,
        order: maxOrder + 1,
        subtasks: item.subtasks.map((st) => ({
          ...st,
          id: crypto.randomUUID(),
          completed: false,
        })),
        recurrence: item.recurrence,
        isRecurring: true,
        seriesId: item.seriesId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
      updatedItems.push(newItem);
    }

    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      items: updatedItems,
      updatedAt: serverTimestamp(),
    });

    return newItem;
  },

  // Delete a todo item
  deleteItem: async (id: string, itemId: string): Promise<void> => {
    const list = await todolistsService.getById(id);
    if (!list) throw new Error('TodoList not found');

    const updatedItems = list.items.filter((item) => item.id !== itemId);

    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      items: updatedItems,
      updatedAt: serverTimestamp(),
    });
  },

  // Reorder items (after drag-and-drop)
  reorderItems: async (id: string, itemIds: string[]): Promise<void> => {
    const list = await todolistsService.getById(id);
    if (!list) throw new Error('TodoList not found');

    const orderMap = new Map(itemIds.map((itemId, index) => [itemId, index]));

    const updatedItems = list.items.map((item) => ({
      ...item,
      order: orderMap.get(item.id) ?? item.order,
    }));

    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      items: updatedItems,
      updatedAt: serverTimestamp(),
    });
  },

  // Update all items (for batch operations)
  updateItems: async (id: string, items: TodoItem[]): Promise<void> => {
    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      items,
      updatedAt: serverTimestamp(),
    });
  },

  // ============================================
  // Subtask Operations
  // ============================================

  // Add a subtask to an item
  addSubtask: async (
    id: string,
    itemId: string,
    input: CreateSubTaskInput
  ): Promise<SubTask> => {
    const list = await todolistsService.getById(id);
    if (!list) throw new Error('TodoList not found');

    const item = list.items.find((i) => i.id === itemId);
    if (!item) throw new Error('TodoItem not found');

    const maxOrder = item.subtasks.reduce((max, st) => Math.max(max, st.order), -1);

    const newSubtask: SubTask = {
      id: crypto.randomUUID(),
      title: input.title,
      completed: false,
      order: maxOrder + 1,
    };

    const updatedItems = list.items.map((i) => {
      if (i.id === itemId) {
        return {
          ...i,
          subtasks: [...i.subtasks, newSubtask],
          updatedAt: Timestamp.now(),
        };
      }
      return i;
    });

    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      items: updatedItems,
      updatedAt: serverTimestamp(),
    });

    return newSubtask;
  },

  // Toggle subtask completion
  toggleSubtask: async (
    id: string,
    itemId: string,
    subtaskId: string
  ): Promise<void> => {
    const list = await todolistsService.getById(id);
    if (!list) throw new Error('TodoList not found');

    const updatedItems = list.items.map((item) => {
      if (item.id === itemId) {
        const updatedSubtasks = item.subtasks.map((st) => {
          if (st.id === subtaskId) {
            return { ...st, completed: !st.completed };
          }
          return st;
        });

        // Check if all subtasks are now completed
        const allCompleted = updatedSubtasks.length > 0 && updatedSubtasks.every((st) => st.completed);

        return {
          ...item,
          subtasks: updatedSubtasks,
          completed: allCompleted ? true : item.completed,
          completedAt: allCompleted && !item.completed ? Timestamp.now() : item.completedAt,
          updatedAt: Timestamp.now(),
        };
      }
      return item;
    });

    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      items: updatedItems,
      updatedAt: serverTimestamp(),
    });
  },

  // Delete a subtask
  deleteSubtask: async (
    id: string,
    itemId: string,
    subtaskId: string
  ): Promise<void> => {
    const list = await todolistsService.getById(id);
    if (!list) throw new Error('TodoList not found');

    const updatedItems = list.items.map((item) => {
      if (item.id === itemId) {
        return {
          ...item,
          subtasks: item.subtasks.filter((st) => st.id !== subtaskId),
          updatedAt: Timestamp.now(),
        };
      }
      return item;
    });

    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      items: updatedItems,
      updatedAt: serverTimestamp(),
    });
  },

  // ============================================
  // Sharing Operations (same pattern as ThinkSpaces)
  // ============================================

  // Invite a user to share
  inviteUser: async (
    id: string,
    input: InviteUserInput,
    invitedBy: string
  ): Promise<void> => {
    const list = await todolistsService.getById(id);
    if (!list) throw new Error('TodoList not found');

    const sharedUser: SharedUser = {
      id: crypto.randomUUID(),
      email: input.email.toLowerCase(),
      displayName: null,
      photoURL: null,
      permission: input.permission,
      status: 'pending',
      // Use Timestamp.now() instead of serverTimestamp() for array items
      invitedAt: Timestamp.now(),
      respondedAt: null,
      invitedBy,
    };

    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      sharedWith: [...list.sharedWith, sharedUser],
      updatedAt: serverTimestamp(),
    });
  },

  // Accept or decline invitation
  respondToInvitation: async (
    id: string,
    userEmail: string,
    accept: boolean,
    user: User
  ): Promise<void> => {
    const list = await todolistsService.getById(id);
    if (!list) throw new Error('TodoList not found');

    const updatedSharedWith = list.sharedWith.map((share) => {
      if (share.email.toLowerCase() === userEmail.toLowerCase()) {
        return {
          ...share,
          status: accept ? 'accepted' : 'declined',
          respondedAt: serverTimestamp(),
          displayName: user.displayName,
          photoURL: user.photoURL,
        };
      }
      return share;
    });

    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      sharedWith: updatedSharedWith,
      updatedAt: serverTimestamp(),
    });
  },

  // Update user permission
  updatePermission: async (
    id: string,
    userEmail: string,
    permission: SharePermission
  ): Promise<void> => {
    const list = await todolistsService.getById(id);
    if (!list) throw new Error('TodoList not found');

    const updatedSharedWith = list.sharedWith.map((share) => {
      if (share.email.toLowerCase() === userEmail.toLowerCase()) {
        return { ...share, permission };
      }
      return share;
    });

    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      sharedWith: updatedSharedWith,
      updatedAt: serverTimestamp(),
    });
  },

  // Remove shared user
  removeSharedUser: async (id: string, userEmail: string): Promise<void> => {
    const list = await todolistsService.getById(id);
    if (!list) throw new Error('TodoList not found');

    const updatedSharedWith = list.sharedWith.filter(
      (share) => share.email.toLowerCase() !== userEmail.toLowerCase()
    );

    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      sharedWith: updatedSharedWith,
      updatedAt: serverTimestamp(),
    });
  },

  // Check if user has access
  checkAccess: (
    list: TodoList,
    userId: string,
    userEmail: string
  ): { hasAccess: boolean; canEdit: boolean; isOwner: boolean } => {
    if (list.ownerId === userId) {
      return { hasAccess: true, canEdit: true, isOwner: true };
    }

    const share = list.sharedWith.find(
      (s) => s.email.toLowerCase() === userEmail.toLowerCase() && s.status === 'accepted'
    );

    if (share) {
      return {
        hasAccess: true,
        canEdit: share.permission === 'write',
        isOwner: false,
      };
    }

    return { hasAccess: false, canEdit: false, isOwner: false };
  },
};
